import { clamp, cloneDeep, max, min } from "lodash";
import { isWebsiteBuilderBox, IWebsiteBuilderBox } from "./container/websiteBuilderBox";
import { TWebsiteBuilderSection } from "./section/websiteBuilderSection";
import { IWebsiteBuilderSectionEntityLayoutData, TWebsiteBuilderSectionEntity, TWebsiteBuilderSectionEntityLayout, websiteBuilderGetSectionEntityActiveLayoutData } from "./websiteBuilderSectionEntity";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "./websiteBuilderNodeGeometry";
import { TWebsiteBuilderDevice } from "../website/shared/device";
import { XYCoord } from "react-dnd";
import { TWebsiteBuilderOverlayLinesRef } from "../designSpace/overlay/WebsiteBuilderOverlay";
import { AppDispatch } from "index";
import { websiteBuilderAddNode, websiteBuilderMoveContainer, websiteBuilderMoveElement, websiteBuilderSelectNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { isWebsiteBuilderStretchContainerData } from "./container/websiteBuilderContainer";
import { isWebsiteBuilderStretchElementData } from "./element/websiteBuilderElement";
import { TWebsiteBuilderActivePageDomNodeRefMap } from "../hooks/activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { TWebsiteBuilderScrollContainerRef } from "../WebsiteBuilderEditor";
import { AnyAction, Dispatch } from "redux";
import { isWebsiteBuilderTab, IWebsiteBuilderTab } from "./tab/websiteBuilderTab";

export type TWebsiteBuilderFreeformParentNode =
  | TWebsiteBuilderSection
  | IWebsiteBuilderBox
  | IWebsiteBuilderTab;

export type TWebsiteBuilderFreeformParentBounds = {
  [K in TWebsiteBuilderDevice]: IWebsiteBuilderNodeDimensions;
}

export interface IWebsiteBuilderFreeformParentAncestry {
  sectionId: string;
  containerId: string;
  gridCellId: string;
  tabId: string;
}

export interface IWebsiteBuilderFreeformParentContext {
  node: TWebsiteBuilderFreeformParentNode;
  bounds: TWebsiteBuilderFreeformParentBounds;
  ancestry: IWebsiteBuilderFreeformParentAncestry;
}

export function isWebsiteBuilderFreeformParentNode(node: any): node is TWebsiteBuilderFreeformParentNode {
  return node?.type === "section" || isWebsiteBuilderBox(node) || isWebsiteBuilderTab(node);
}

function getFreeformNodeZIndex(node: TWebsiteBuilderSectionEntity): number {
  return node.type === "element" ? node.element.zIndex : node.container.zIndex;
}

function getFreeformParentMinZIndex(node: TWebsiteBuilderFreeformParentNode): number {
  const zIndices = node.children.map(getFreeformNodeZIndex);
  const minZIndex = min(zIndices);

  return minZIndex;
}

function getFreeformParentMaxZIndex(node: TWebsiteBuilderFreeformParentNode): number {
  const zIndices = node.children.map(getFreeformNodeZIndex);
  const maxZIndex = max(zIndices);

  return maxZIndex;
}

export function websiteBuilderGetFreeformParentNextZIndex(node: TWebsiteBuilderFreeformParentNode): number {
  const maxZIndex = getFreeformParentMaxZIndex(node);
  const nextZIndex = maxZIndex == null ? 0 : maxZIndex + 1;

  return nextZIndex;
}

export function websiteBuilderCanBringToBackInFreeformParent(
  node: TWebsiteBuilderSectionEntity,
  parentNode: TWebsiteBuilderFreeformParentNode,
) {
  const zIndex = getFreeformNodeZIndex(node);
  const minZIndex = getFreeformParentMinZIndex(parentNode);

  return minZIndex != null && parentNode.children.length > 1 && zIndex !== minZIndex;
}

export function websiteBuilderCanBringToFrontInFreeformParent(
  node: TWebsiteBuilderSectionEntity,
  parentNode: TWebsiteBuilderFreeformParentNode,
) {
  const zIndex = getFreeformNodeZIndex(node);
  const maxZIndex = getFreeformParentMaxZIndex(parentNode);

  return maxZIndex != null && parentNode.children.length > 1 && zIndex !== maxZIndex;
}

function isInvalidFreeformParentNodeChild(node: TWebsiteBuilderSectionEntity, parentNode: TWebsiteBuilderFreeformParentNode) {
  return parentNode.type !== "section" && (node.type !== "element" || (isWebsiteBuilderStretchElementData(node.element) && node.element.stretch.enabled));
}

export function websiteBuilderCalculateSectionEntityInsertLayout(
  position: IWebsiteBuilderNodePosition,
  prevLayout: TWebsiteBuilderSectionEntityLayout,
  device: TWebsiteBuilderDevice,
  desktopBound: IWebsiteBuilderNodeDimensions,
  tabletBound: IWebsiteBuilderNodeDimensions,
  mobileBound: IWebsiteBuilderNodeDimensions,
): TWebsiteBuilderSectionEntityLayout {
  type TXAnchorType = "left" | "center" | "right";

  type TXAnchor = {
    type: TXAnchorType;
    percent: number;
  }

  type TYAnchorType = "top" | "center" | "bottom";

  type TYAnchor = {
    type: TYAnchorType;
    percent: number;
  }

  type TAnchor = {
    x: TXAnchor;
    y: TYAnchor;
  }

  const CENTER_THRESHOLD = 0.1;

  function calculateAnchor(
    position: IWebsiteBuilderNodePosition,
    dimensions: IWebsiteBuilderNodeDimensions,
    bound: IWebsiteBuilderNodeDimensions,
  ): TAnchor {
    let xAnchorType: TXAnchorType = "left";
    let yAnchorType: TYAnchorType = "top";

    let xAnchorPercent = position.x / bound.width;
    let yAnchorPercent = position.y / bound.height;

    const centerX = position.x + dimensions.width / 2;
    const centerY = position.y + dimensions.height / 2;

    if (Math.abs(centerX - (bound.width / 2)) / bound.width < CENTER_THRESHOLD) {
      xAnchorType = "center";
      xAnchorPercent = centerX / bound.width;
    } else if (centerX > bound.width / 2) {
      xAnchorType = "right";
      xAnchorPercent = (bound.width - (position.x + dimensions.width)) / bound.width;
    }

    if (Math.abs(centerY - (bound.height / 2)) / bound.height < CENTER_THRESHOLD) {
      yAnchorType = "center";
      yAnchorPercent = centerY / bound.height;
    } else if (centerY > bound.height / 2) {
      yAnchorType = "bottom";
      yAnchorPercent = (bound.height - (position.y + dimensions.height)) / bound.height;
    }

    const anchor: TAnchor = {
      x: {
        type: xAnchorType,
        percent: clamp(xAnchorPercent, 0, 1),
      },
      y: {
        type: yAnchorType,
        percent: clamp(yAnchorPercent, 0, 1),
      },
    };

    return anchor;
  }

  function calculatePosition(
    dimensions: IWebsiteBuilderNodeDimensions,
    bound: IWebsiteBuilderNodeDimensions,
    anchor: TAnchor,
  ): IWebsiteBuilderNodePosition {
    const xOffset = anchor.x.percent * bound.width;
    const yOffset = anchor.y.percent * bound.height;
    
    let x = xOffset;
    let y = yOffset;

    if (anchor.x.type === "center") {
      x = xOffset - (dimensions.width / 2);
    } else if (anchor.x.type === "right") {
      x = bound.width - xOffset - dimensions.width;
    }

    if (anchor.y.type === "center") {
      y = yOffset - (dimensions.height / 2);
    } else if (anchor.y.type === "bottom") {
      y = bound.height - yOffset - dimensions.height;
    }

    const position: IWebsiteBuilderNodePosition = {
      x: clamp(x, 0, bound.width - dimensions.width),
      y: clamp(y, 0, bound.height - dimensions.height),
    };

    return position;
  }

  const desktopDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(prevLayout.desktop.dimensions.width, desktopBound.width),
    height: Math.min(prevLayout.desktop.dimensions.height, desktopBound.height),
  };

  const tabletDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(prevLayout.tablet.dimensions.width, tabletBound.width),
    height: Math.min(prevLayout.tablet.dimensions.height, tabletBound.height),
  };

  const mobileDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(prevLayout.mobile.dimensions.width, mobileBound.width),
    height: Math.min(prevLayout.mobile.dimensions.height, mobileBound.height),
  };

  const anchor =
    device === "desktop"
      ? calculateAnchor(position, desktopDimensions, desktopBound)
      : device === "tablet"
        ? calculateAnchor(position, tabletDimensions, tabletBound)
        : calculateAnchor(position, mobileDimensions, mobileBound);
  
  const nextLayout: TWebsiteBuilderSectionEntityLayout = {
    desktop: {
      position: calculatePosition(desktopDimensions, desktopBound, anchor),
      dimensions: desktopDimensions,
    },
    tablet: {
      position: calculatePosition(tabletDimensions, tabletBound, anchor),
      dimensions: tabletDimensions,
    },
    mobile: {
      position: calculatePosition(mobileDimensions, mobileBound, anchor),
      dimensions: mobileDimensions,
    },
  };

  return nextLayout;
}

export interface IWebsiteBuilderDropSectionEntityInFreeformDomainRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function websiteBuilderDropSectionEntityInFreeform(
  item: TWebsiteBuilderSectionEntity,
  parentContext: IWebsiteBuilderFreeformParentContext,
  domainRect: IWebsiteBuilderDropSectionEntityInFreeformDomainRect,
  clientOffset: XYCoord,
  activePageId: number,
  device: TWebsiteBuilderDevice,
  overlayLinesRef: TWebsiteBuilderOverlayLinesRef,
  dispatch: AppDispatch,
) {
  if (isInvalidFreeformParentNodeChild(item, parentContext.node)) {
    return;
  }

  const stretchedItem =
    item.type === "element" ?
      isWebsiteBuilderStretchElementData(item.element) && item.element.stretch.enabled :
      isWebsiteBuilderStretchContainerData(item.container) && item.container.stretch.enabled;

  const activeLayoutData = websiteBuilderGetSectionEntityActiveLayoutData(item, device);

  const width = activeLayoutData.dimensions.width;
  const height = activeLayoutData.dimensions.height;

  const position: IWebsiteBuilderNodePosition = {
    x: clientOffset.x - width / 2 - domainRect.left,
    y: clientOffset.y - height / 2 - domainRect.top,
  };

  if (overlayLinesRef.current.horizontal != null) {
    const { anchor, y1 } = overlayLinesRef.current.horizontal;

    switch (anchor) {
      case "top": {
        position.y = y1 - domainRect.top;
        break;
      }
      case "center": {
        position.y = y1 - height / 2 - domainRect.top;
        break;
      }
      case "bottom": {
        position.y = y1 - height - domainRect.top;
        break;
      }
    }
  }
  if (overlayLinesRef.current.vertical != null) {
    const { anchor, x1 } = overlayLinesRef.current.vertical;
  
    switch (anchor) {
      case "left": {
        position.x = x1 - domainRect.left;
        break;
      }
      case "center": {
        position.x = x1 - width / 2 - domainRect.left;
        break;
      }
      case "right": {
        position.x = x1 - width - domainRect.left;
        break;
      }
    }
  }

  const clampedPosition: IWebsiteBuilderNodePosition = {
    x: clamp(
      position.x,
      0,
      Math.max(domainRect.width - width, 0),
    ),
    y: clamp(
      position.y,
      0,
      Math.max(domainRect.height - height, 0),
    ),
  };

  const prevLayout = item.type === "element" ? item.element.layout : item.container.layout;

  const updatedLayout = websiteBuilderCalculateSectionEntityInsertLayout(
    clampedPosition,
    prevLayout,
    device,
    parentContext.bounds.desktop,
    parentContext.bounds.tablet,
    parentContext.bounds.mobile,
  );

  if (stretchedItem) {
    updatedLayout.desktop.position.x = 0;
    updatedLayout.desktop.dimensions.width = parentContext.bounds.desktop.width;

    updatedLayout.tablet.position.x = 0;
    updatedLayout.tablet.dimensions.width = parentContext.bounds.tablet.width;

    updatedLayout.mobile.position.x = 0;
    updatedLayout.mobile.dimensions.width = parentContext.bounds.mobile.width;
  }

  const updatedItem = cloneDeep(item);

  if (updatedItem.type === "element") {
    updatedItem.element.layout = updatedLayout;
    updatedItem.element.zIndex = websiteBuilderGetFreeformParentNextZIndex(parentContext.node);
  } else if (updatedItem.type === "container") {
    updatedItem.container.layout = updatedLayout;
    updatedItem.container.zIndex = websiteBuilderGetFreeformParentNextZIndex(parentContext.node);
  }

  const addNodeAction = websiteBuilderAddNode(activePageId, device, updatedItem.id, {
    snapshot: {
      inverseScrollNodeId: parentContext.node.id,
      parentId: parentContext.node.id,
      node: updatedItem,
      index: parentContext.node.children.length,
    },
  });

  if (addNodeAction == null) {
    return;
  }

  dispatch(addNodeAction);

  if (updatedItem.type === "element") {
    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          type: "element",
          sectionId: parentContext.ancestry.sectionId,
          containerId: parentContext.ancestry.containerId,
          gridCellId: parentContext.ancestry.gridCellId,
          tabId: parentContext.ancestry.tabId,
          id: updatedItem.id,
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  } else if (updatedItem.type === "container") {
    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          type: "container",
          sectionId: parentContext.ancestry.sectionId,
          id: updatedItem.id,
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  }
}

export function websiteBuilderPasteSectionEntityInFreeform(
  copiedNode: TWebsiteBuilderSectionEntity,
  parentContext: IWebsiteBuilderFreeformParentContext,
  pageId: number,
  device: TWebsiteBuilderDevice,
  activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
  dispatch: AppDispatch,
) {
  if (isInvalidFreeformParentNodeChild(copiedNode, parentContext.node)) {
    return;
  }

  const nodeToPaste = cloneDeep(copiedNode);

  const parentRect = activePageDomNodeRefMap.current.get(parentContext.node.id)?.getBoundingClientRect();
  const scrollContainerRect = scrollContainerRef.current?.getBoundingClientRect();

  if (parentRect == null || scrollContainerRect == null) {
    return;
  }

  const stretchedNode =
    nodeToPaste.type === "element" ?
      isWebsiteBuilderStretchElementData(nodeToPaste.element) && nodeToPaste.element.stretch.enabled :
      isWebsiteBuilderStretchContainerData(nodeToPaste.container) && nodeToPaste.container.stretch.enabled;

  const desktopLayoutData = nodeToPaste.type === "element" ? nodeToPaste.element.layout.desktop : nodeToPaste.container.layout.desktop;
  const tabletLayoutData = nodeToPaste.type === "element" ? nodeToPaste.element.layout.tablet : nodeToPaste.container.layout.tablet;
  const mobileLayoutData = nodeToPaste.type === "element" ? nodeToPaste.element.layout.mobile : nodeToPaste.container.layout.mobile;

  const desktopDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(desktopLayoutData.dimensions.width, parentContext.bounds.desktop.width),
    height: Math.min(desktopLayoutData.dimensions.height, parentContext.bounds.desktop.height),
  };

  const tabletDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(tabletLayoutData.dimensions.width, parentContext.bounds.tablet.width),
    height: Math.min(tabletLayoutData.dimensions.height, parentContext.bounds.tablet.height),
  };

  const mobileDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(mobileLayoutData.dimensions.width, parentContext.bounds.mobile.width),
    height: Math.min(mobileLayoutData.dimensions.height, parentContext.bounds.mobile.height),
  };

  const desktopPosition: IWebsiteBuilderNodePosition = {
    x: parentContext.bounds.desktop.width / 2 - desktopDimensions.width / 2,
    y: parentContext.bounds.desktop.height / 2 - desktopDimensions.height / 2,
  };

  const tabletPosition: IWebsiteBuilderNodePosition = {
    x: parentContext.bounds.tablet.width / 2 - tabletDimensions.width / 2,
    y: parentContext.bounds.tablet.height / 2 - tabletDimensions.height / 2,
  };

  const mobilePosition: IWebsiteBuilderNodePosition = {
    x: parentContext.bounds.mobile.width / 2 - mobileDimensions.width / 2,
    y: parentContext.bounds.mobile.height / 2 - mobileDimensions.height / 2,
  };

  if (stretchedNode) {
    desktopPosition.x = 0;
    desktopDimensions.width = parentContext.bounds.desktop.width;

    tabletPosition.x = 0;
    tabletDimensions.width = parentContext.bounds.tablet.width;

    mobilePosition.x = 0;
    mobileDimensions.width = parentContext.bounds.mobile.width;
  }

  let activePosition: IWebsiteBuilderNodePosition = null;
  let activeDimensions: IWebsiteBuilderNodeDimensions = null;

  if (device === "desktop") {
    activePosition = desktopPosition;
    activeDimensions = desktopDimensions;
  } else if (device === "tablet") {
    activePosition = tabletPosition;
    activeDimensions = tabletDimensions;
  } else {
    activePosition = mobilePosition;
    activeDimensions = mobileDimensions;
  }

  const screenPositionTop = parentRect.top + activePosition.y;
  const screenPositionBottom = screenPositionTop + activeDimensions.height;

  if (screenPositionTop < scrollContainerRect.top) {
    const delta = scrollContainerRect.top - screenPositionTop;
    activePosition.y += delta;
  } else if (screenPositionBottom > scrollContainerRect.bottom) {
    const delta = screenPositionBottom - scrollContainerRect.bottom;
    activePosition.y -= delta;
  }

  const desktopUpperY = parentContext.bounds.desktop.height - desktopDimensions.height;
  const tabletUpperY = parentContext.bounds.tablet.height - tabletDimensions.height;
  const mobileUpperY = parentContext.bounds.mobile.height - mobileDimensions.height;

  desktopPosition.y = clamp(desktopPosition.y, 0, desktopUpperY);
  tabletPosition.y = clamp(tabletPosition.y, 0, tabletUpperY);
  mobilePosition.y = clamp(mobilePosition.y, 0, mobileUpperY);

  const layout: TWebsiteBuilderSectionEntityLayout = {
    desktop: {
      position: desktopPosition,
      dimensions: desktopDimensions,
    },
    tablet: {
      position: tabletPosition,
      dimensions: tabletDimensions,
    },
    mobile: {
      position: mobilePosition,
      dimensions: mobileDimensions,
    },
  };

  if (nodeToPaste.type === "element") {
    nodeToPaste.element.layout = layout;
    nodeToPaste.element.zIndex = websiteBuilderGetFreeformParentNextZIndex(parentContext.node);
  } else if (nodeToPaste.type === "container") {
    nodeToPaste.container.layout = layout;
    nodeToPaste.container.zIndex = websiteBuilderGetFreeformParentNextZIndex(parentContext.node);
  }

  const addNodeAction = websiteBuilderAddNode(pageId, device, nodeToPaste.id, {
    snapshot: {
      inverseScrollNodeId: parentContext.node.id,
      parentId: parentContext.node.id,
      node: nodeToPaste,
      index: parentContext.node.children.length,
    },
  });

  if (addNodeAction == null) {
    return;
  }

  dispatch(addNodeAction);

  if (nodeToPaste.type === "element") {
    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          type: "element",
          sectionId: parentContext.ancestry.sectionId,
          containerId: parentContext.ancestry.containerId,
          gridCellId: parentContext.ancestry.gridCellId,
          tabId: parentContext.ancestry.tabId,
          id: nodeToPaste.id,
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  } else if (nodeToPaste.type === "container") {
    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          type: "container",
          sectionId: parentContext.ancestry.sectionId,
          id: nodeToPaste.id,
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  }
}

export type TWebsiteBuilderNudgeSectionEntityInFreeformDirection = "up" | "right" | "down" | "left";

export function websiteBuilderNudgeSectionEntityInFreeform(
  direction: TWebsiteBuilderNudgeSectionEntityInFreeformDirection,
  shift: boolean,
  node: TWebsiteBuilderSectionEntity,
  parentContext: IWebsiteBuilderFreeformParentContext,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  if (isInvalidFreeformParentNodeChild(node, parentContext.node)) {
    return;
  }

  const offset = shift ? 10 : 1;

  const activeLayoutData = websiteBuilderGetSectionEntityActiveLayoutData(node, device);

  const width = activeLayoutData.dimensions.width;
  const height = activeLayoutData.dimensions.height;
  const zIndex = node.type === "element" ? node.element.zIndex : node.container.zIndex;

  let x = activeLayoutData.position.x;
  let y = activeLayoutData.position.y;

  switch (direction) {
    case "up": {
      y -= offset;
      break;
    }
    case "right": {
      x += offset;
      break;
    }
    case "down": {
      y += offset;
      break;
    }
    case "left": {
      x -= offset;
      break;
    }
  }

  const parentWidth = parentContext.bounds[device].width;
  const parentHeight = parentContext.bounds[device].height;

  const upperX = calculateSectionEntityUpperX(width, parentWidth);
  const upperY = calculateSectionEntityUpperY(height, parentHeight);

  if (x < 0 || y < 0 || x > upperX || y > upperY) {
    return;
  }

  const index = parentContext.node.children.findIndex(child => child.id === node.id);

  if (node.type === "element") {
    const prevLayout = cloneDeep(node.element.layout);
    const nextLayout = cloneDeep(prevLayout);
    nextLayout[device].position = { x, y };

    dispatch(websiteBuilderMoveElement(pageId, device, node.id, {
      id: node.id,
      prevIndex: index,
      nextIndex: index,
      prevZIndex: zIndex,
      nextZIndex: zIndex,
      prevSectionId: parentContext.ancestry.sectionId,
      nextSectionId: parentContext.ancestry.sectionId,
      prevContainerId: parentContext.ancestry.containerId,
      nextContainerId: parentContext.ancestry.containerId,
      prevGridCellId: parentContext.ancestry.gridCellId,
      nextGridCellId: parentContext.ancestry.gridCellId,
      prevTabId: parentContext.ancestry.tabId,
      nextTabId: parentContext.ancestry.tabId,
      prevLayout,
      nextLayout,
    }));
  } else {
    const prevLayout = cloneDeep(node.container.layout);
    const nextLayout = cloneDeep(prevLayout);
    nextLayout[device].position = { x, y };

    dispatch(websiteBuilderMoveContainer(pageId, device, node.id, {
      id: node.id,
      prevIndex: index,
      nextIndex: index,
      prevZIndex: zIndex,
      nextZIndex: zIndex,
      prevSectionId: parentContext.ancestry.sectionId,
      nextSectionId: parentContext.ancestry.sectionId,
      prevLayout,
      nextLayout,
    }));
  }
}

const SECTION_ENTITY_CREATION_OFFSET = 10;

function calculateSectionEntityUpperX(width: number, widthBound: number) {
  return widthBound - width;
}

function calculateSectionEntityUpperY(height: number, heightBound: number) {
  return heightBound - height;
}

function calculateSectionEntityOffsetPosition(
  originalPosition: IWebsiteBuilderNodePosition,
  stretchedNode: boolean,
  upperX: number,
  upperY: number,
): IWebsiteBuilderNodePosition {
  const offsetX = originalPosition.x + SECTION_ENTITY_CREATION_OFFSET;
  const offsetY = originalPosition.y + SECTION_ENTITY_CREATION_OFFSET;

  if (stretchedNode) {
    if (offsetY > upperY) {
      return null;
    } else {
      return {
        x: 0,
        y: offsetY,
      };
    }
  } else {
    if (offsetX > upperX && offsetY > upperY) {
      return null;
    } else {
      return {
        x: Math.min(offsetX, upperX),
        y: Math.min(offsetY, upperY),
      };
    }
  }
}

function calculateSectionEntityInsertPosition(
  targetLayoutData: IWebsiteBuilderSectionEntityLayoutData,
  stretchedNode: boolean,
  upperX: number,
  upperY: number,
): IWebsiteBuilderNodePosition {
  const position: IWebsiteBuilderNodePosition = { ...targetLayoutData.position };

  if (stretchedNode) {
    let x = 0;
    let y = targetLayoutData.position.y + targetLayoutData.dimensions.height + SECTION_ENTITY_CREATION_OFFSET;

    if (y > upperY) {
      const offsetPosition = calculateSectionEntityOffsetPosition(
        targetLayoutData.position,
        stretchedNode,
        upperX,
        upperY,
      );

      if (offsetPosition == null) {
        return null;
      }

      x = offsetPosition.x;
      y = offsetPosition.y;
    }

    position.x = x;
    position.y = y;
  } else {
    if (targetLayoutData.position.x > upperX || targetLayoutData.position.y > upperY) {
      return null;
    }

    let x = targetLayoutData.position.x + targetLayoutData.dimensions.width + SECTION_ENTITY_CREATION_OFFSET;
    let y = targetLayoutData.position.y;

    if (x > upperX) {
      x = targetLayoutData.position.x;
      y = targetLayoutData.position.y + targetLayoutData.dimensions.height + SECTION_ENTITY_CREATION_OFFSET;

      if (y > upperY) {
        const offsetPosition = calculateSectionEntityOffsetPosition(
          targetLayoutData.position,
          stretchedNode,
          upperX,
          upperY,
        );

        if (offsetPosition == null) {
          return null;
        }

        x = offsetPosition.x;
        y = offsetPosition.y;
      }
    }

    position.x = x;
    position.y = y;
  }

  return position;
}

function calculateSectionEntityClampedLayout(
  desktopPosition: IWebsiteBuilderNodePosition,
  tabletPosition: IWebsiteBuilderNodePosition,
  mobilePosition: IWebsiteBuilderNodePosition,
  desktopDimensions: IWebsiteBuilderNodeDimensions,
  tabletDimensions: IWebsiteBuilderNodeDimensions,
  mobileDimensions: IWebsiteBuilderNodeDimensions,
  bounds: TWebsiteBuilderFreeformParentBounds,
): TWebsiteBuilderSectionEntityLayout {
  const layout: TWebsiteBuilderSectionEntityLayout = {
    desktop: {
      position: {
        x: clamp(
          desktopPosition.x,
          0,
          bounds.desktop.width - desktopDimensions.width,
        ),
        y: clamp(
          desktopPosition.y,
          0,
          bounds.desktop.height - desktopDimensions.height,
        ),
      },
      dimensions: desktopDimensions,
    },
    tablet: {
      position: {
        x: clamp(
          tabletPosition.x,
          0,
          bounds.tablet.width - tabletDimensions.width,
        ),
        y: clamp(
          tabletPosition.y,
          0,
          bounds.tablet.height - tabletDimensions.height,
        ),
      },
      dimensions: tabletDimensions,
    },
    mobile: {
      position: {
        x: clamp(
          mobilePosition.x,
          0,
          bounds.mobile.width - mobileDimensions.width,
        ),
        y: clamp(
          mobilePosition.y,
          0,
          bounds.mobile.height - mobileDimensions.height,
        ),
      },
      dimensions: mobileDimensions,
    },
  };

  return layout;
}

function addSectionEntityInFreeform(
  node: TWebsiteBuilderSectionEntity,
  parentContext: IWebsiteBuilderFreeformParentContext,
  desktopPosition: IWebsiteBuilderNodePosition,
  tabletPosition: IWebsiteBuilderNodePosition,
  mobilePosition: IWebsiteBuilderNodePosition,
  desktopDimensions: IWebsiteBuilderNodeDimensions,
  tabletDimensions: IWebsiteBuilderNodeDimensions,
  mobileDimensions: IWebsiteBuilderNodeDimensions,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  const layout = calculateSectionEntityClampedLayout(
    desktopPosition,
    tabletPosition,
    mobilePosition,
    desktopDimensions,
    tabletDimensions,
    mobileDimensions,
    parentContext.bounds,
  );

  if (node.type === "element") {
    node.element.layout = layout;
    node.element.zIndex = websiteBuilderGetFreeformParentNextZIndex(parentContext.node);
  } else if (node.type === "container") {
    node.container.layout = layout;
    node.container.zIndex = websiteBuilderGetFreeformParentNextZIndex(parentContext.node);
  }

  const addNodeAction = websiteBuilderAddNode(pageId, device, node.id, {
    snapshot: {
      inverseScrollNodeId: parentContext.node.id,
      parentId: parentContext.node.id,
      node,
      index: parentContext.node.children.length,
    },
  });

  if (addNodeAction == null) {
    return;
  }

  dispatch(addNodeAction);

  if (node.type === "element") {
    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          type: "element",
          sectionId: parentContext.ancestry.sectionId,
          containerId: parentContext.ancestry.containerId,
          gridCellId: parentContext.ancestry.gridCellId,
          tabId: parentContext.ancestry.tabId,
          id: node.id,
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  } else if (node.type === "container") {
    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          type: "container",
          sectionId: parentContext.ancestry.sectionId,
          id: node.id,
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  }
}

export function websiteBuilderDuplicateSectionEntityInFreeform(
  node: TWebsiteBuilderSectionEntity,
  parentContext: IWebsiteBuilderFreeformParentContext,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  if (isInvalidFreeformParentNodeChild(node, parentContext.node)) {
    return;
  }

  const nodeToDuplicate = cloneDeep(node);

  const stretchedNode =
    nodeToDuplicate.type === "element" ?
      isWebsiteBuilderStretchElementData(nodeToDuplicate.element) && nodeToDuplicate.element.stretch.enabled :
      isWebsiteBuilderStretchContainerData(nodeToDuplicate.container) && nodeToDuplicate.container.stretch.enabled;

  const desktopLayoutData = nodeToDuplicate.type === "element" ? nodeToDuplicate.element.layout.desktop : nodeToDuplicate.container.layout.desktop;
  const tabletLayoutData = nodeToDuplicate.type === "element" ? nodeToDuplicate.element.layout.tablet : nodeToDuplicate.container.layout.tablet;
  const mobileLayoutData = nodeToDuplicate.type === "element" ? nodeToDuplicate.element.layout.mobile : nodeToDuplicate.container.layout.mobile;

  const desktopUpperX = calculateSectionEntityUpperX(desktopLayoutData.dimensions.width, parentContext.bounds.desktop.width);
  const desktopUpperY = calculateSectionEntityUpperY(desktopLayoutData.dimensions.height, parentContext.bounds.desktop.height);

  const tabletUpperX = calculateSectionEntityUpperX(tabletLayoutData.dimensions.width, parentContext.bounds.tablet.width);
  const tabletUpperY = calculateSectionEntityUpperY(tabletLayoutData.dimensions.height, parentContext.bounds.tablet.height);

  const mobileUpperX = calculateSectionEntityUpperX(mobileLayoutData.dimensions.width, parentContext.bounds.mobile.width);
  const mobileUpperY = calculateSectionEntityUpperY(mobileLayoutData.dimensions.height, parentContext.bounds.mobile.height);

  let desktopPosition = calculateSectionEntityOffsetPosition(
    desktopLayoutData.position,
    stretchedNode,
    desktopUpperX,
    desktopUpperY,
  );

  let tabletPosition = calculateSectionEntityOffsetPosition(
    tabletLayoutData.position,
    stretchedNode,
    tabletUpperX,
    tabletUpperY,
  );

  let mobilePosition = calculateSectionEntityOffsetPosition(
    mobileLayoutData.position,
    stretchedNode,
    mobileUpperX,
    mobileUpperY,
  );

  if (device === "desktop" && desktopPosition == null) {
    return;
  } else if (device === "tablet" && tabletPosition == null) {
    return;
  } else if (device === "mobile" && mobilePosition == null) {
    return;
  }

  desktopPosition ??= { ...desktopLayoutData.position };
  tabletPosition ??= { ...tabletLayoutData.position };
  mobilePosition ??= { ...mobileLayoutData.position };

  addSectionEntityInFreeform(
    nodeToDuplicate,
    parentContext,
    desktopPosition,
    tabletPosition,
    mobilePosition,
    desktopLayoutData.dimensions,
    tabletLayoutData.dimensions,
    mobileLayoutData.dimensions,
    pageId,
    device,
    dispatch,
  );
}

export function websiteBuilderInsertSectionEntityInFreeform(
  node: TWebsiteBuilderSectionEntity,
  targetNode: TWebsiteBuilderSectionEntity,
  targetParentContext: IWebsiteBuilderFreeformParentContext,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  if (
    isInvalidFreeformParentNodeChild(node, targetParentContext.node) ||
    isInvalidFreeformParentNodeChild(targetNode, targetParentContext.node)
  ) {
    return;
  }

  const nodeToInsert = cloneDeep(node);

  const stretchedNode =
    nodeToInsert.type === "element" ?
      isWebsiteBuilderStretchElementData(nodeToInsert.element) && nodeToInsert.element.stretch.enabled :
      isWebsiteBuilderStretchContainerData(nodeToInsert.container) && nodeToInsert.container.stretch.enabled;

  const targetDesktopLayoutData = targetNode.type === "element" ? targetNode.element.layout.desktop : targetNode.container.layout.desktop;
  const targetTabletLayoutData = targetNode.type === "element" ? targetNode.element.layout.tablet : targetNode.container.layout.tablet;
  const targetMobileLayoutData = targetNode.type === "element" ? targetNode.element.layout.mobile : targetNode.container.layout.mobile;

  const desktopLayoutData = nodeToInsert.type === "element" ? nodeToInsert.element.layout.desktop : nodeToInsert.container.layout.desktop;
  const tabletLayoutData = nodeToInsert.type === "element" ? nodeToInsert.element.layout.tablet : nodeToInsert.container.layout.tablet;
  const mobileLayoutData = nodeToInsert.type === "element" ? nodeToInsert.element.layout.mobile : nodeToInsert.container.layout.mobile;

  const desktopDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(desktopLayoutData.dimensions.width, targetParentContext.bounds.desktop.width),
    height: Math.min(desktopLayoutData.dimensions.height, targetParentContext.bounds.desktop.height),
  };

  const tabletDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(tabletLayoutData.dimensions.width, targetParentContext.bounds.tablet.width),
    height: Math.min(tabletLayoutData.dimensions.height, targetParentContext.bounds.tablet.height),
  };

  const mobileDimensions: IWebsiteBuilderNodeDimensions = {
    width: Math.min(mobileLayoutData.dimensions.width, targetParentContext.bounds.mobile.width),
    height: Math.min(mobileLayoutData.dimensions.height, targetParentContext.bounds.mobile.height),
  };

  const desktopUpperX = calculateSectionEntityUpperX(desktopDimensions.width, targetParentContext.bounds.desktop.width);
  const desktopUpperY = calculateSectionEntityUpperY(desktopDimensions.height, targetParentContext.bounds.desktop.height);

  const tabletUpperX = calculateSectionEntityUpperX(tabletDimensions.width, targetParentContext.bounds.tablet.width);
  const tabletUpperY = calculateSectionEntityUpperY(tabletDimensions.height, targetParentContext.bounds.tablet.height);

  const mobileUpperX = calculateSectionEntityUpperX(mobileDimensions.width, targetParentContext.bounds.mobile.width);
  const mobileUpperY = calculateSectionEntityUpperY(mobileDimensions.height, targetParentContext.bounds.mobile.height);

  let desktopPosition = calculateSectionEntityInsertPosition(
    targetDesktopLayoutData,
    stretchedNode,
    desktopUpperX,
    desktopUpperY,
  );
  
  let tabletPosition = calculateSectionEntityInsertPosition(
    targetTabletLayoutData,
    stretchedNode,
    tabletUpperX,
    tabletUpperY,
  );

  let mobilePosition = calculateSectionEntityInsertPosition(
    targetMobileLayoutData,
    stretchedNode,
    mobileUpperX,
    mobileUpperY,
  );

  if (device === "desktop" && desktopPosition == null) {
    return;
  } else if (device === "tablet" && tabletPosition == null) {
    return;
  } else if (device === "mobile" && mobilePosition == null) {
    return;
  }

  desktopPosition ??= { ...targetDesktopLayoutData.position };
  tabletPosition ??= { ...targetTabletLayoutData.position };
  mobilePosition ??= { ...targetMobileLayoutData.position };

  addSectionEntityInFreeform(
    nodeToInsert,
    targetParentContext,
    desktopPosition,
    tabletPosition,
    mobilePosition,
    desktopDimensions,
    tabletDimensions,
    mobileDimensions,
    pageId,
    device,
    dispatch,
  );
}
